// import { faker } from '@faker-js/faker';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box,  Paper, Button, Typography, CardContent, Skeleton } from '@mui/material';
// utils
//
import { varFadeInRight,  varBounceInUp, MotionContainer } from '../../../components/animate';
import { CarouselControlsArrowsIndex } from '../../../components/carousel/controls';


// redux
import { useDispatch, useSelector } from '../../../redux/store';

// data
import { getBanners } from '../../../redux/slices/home';

// ----------------------------------------------------------------------

const CarouselImgStyle = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const CarouseSublImgStyle =styled('img')(({ theme }) => ({
  right: 100,
  top: 30,
  height: '100%',
  objectFit: 'scale-down',
  position: 'absolute',
  [theme.breakpoints.down(480)]: {
    right: 5,
    top: 100,
    height: '80%',
   
  },
}));

const Styledtext = styled(Typography)({
  height: 104,
  width: '100',
  overflow: 'hidden',
  WebkitLineClamp: 4,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});
// ----------------------------------------------------------------------

// const CAROUSELS = [...Array(5)].map((_, index) => {
//   const setIndex = index + 1;
//   return {
//     title: faker.company.name(),
//     description: faker.lorem.paragraphs(),
//     smimage: `/assets/images/asap/banner_person_01.png`,
//     image: `/assets/images/covers/cover_${setIndex}.jpg`,
//   };
// });

CarouselItem.propTypes = {
  item: PropTypes.object,
  isActive: PropTypes.bool,
  isFirst: PropTypes.bool,
};

function CarouselItem({ item, isActive, isFirst }) {
  const theme = useTheme();
  const { image, title,body, smimage, url } = item;

  return (
    <Paper
      sx={{
        position: 'relative',
        paddingTop: { xs: '100%', md: '35%' },
      }}
    >
      {/* The first slide is the LCP element, so it must not be deferred. Every
          other slide is off-screen and can be lazy. react-slick renders all
          slides at once, so without this the whole carousel's images compete for
          the same connection as the one the visitor actually sees. */}
      <CarouselImgStyle
        alt={title}
        src={image}
        loading={isFirst ? 'eager' : 'lazy'}
        fetchpriority={isFirst ? 'high' : 'auto'}
        decoding="async"
      />
      <Box
        sx={{
          top: 0,
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundImage: `linear-gradient(to top, ${theme.palette.grey[900]} 0%,${alpha(
            theme.palette.grey[900],
            0
          )} 100%)`,
        }}
      />
      <CardContent
        sx={{
          top: {xs:10, md:50},
          width: '100%',
          maxWidth: 480,
          textAlign: 'left',
          position: 'absolute',
          color: 'common.white',
        }}
      >
        <MotionContainer open={isActive}>
          <motion.div variants={varFadeInRight}>
            <Typography component="h1" variant="h3" gutterBottom>
              {item.title}
            </Typography>
          </motion.div>
          <motion.div variants={varFadeInRight}>
            <Styledtext variant="body2" gutterBottom>
              {body}
            </Styledtext>
          </motion.div>
          <motion.div variants={varFadeInRight}>
            <Button to={url} component={RouterLink} variant="contained" sx={{ mt: 3 }}>
              View More
            </Button>
          </motion.div>
        </MotionContainer>
      </CardContent>
      <MotionContainer open={isActive}>
      <motion.div variants={varBounceInUp} transition={{delay:600}} >
      <CarouseSublImgStyle alt={title} src={smimage} />
      </motion.div>
      </MotionContainer>
    </Paper>
  );
}
export default function HeroWidget() {
  const theme = useTheme();

  const dispatch = useDispatch();
  const carouselRef = useRef();
  const { banners, error } = useSelector((state) => state.home);

  // `isLoading` alone cannot tell "request not dispatched yet" from "request
  // finished and the API returned no banners": both leave isLoading false with
  // an empty list, and the second one must not hold a skeleton on screen
  // forever. Track the settle explicitly.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let active = true;

    Promise.resolve(dispatch(getBanners())).finally(() => {
      if (active) {
        setSettled(true);
      }
    });

    return () => {
      active = false;
    };
  }, [dispatch]);

  // Was `theme.direction === 'rtl' ? banners? banners.length - 1 : 0:0`, which
  // parsed as `rtl ? (banners ? banners.length - 1 : 0) : 0` and read as a typo.
  // The initial index is 0 either way; slick's rtl option already flips the
  // visual order, so the RTL branch was never needed.
  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = Array.isArray(banners) ? banners : [];

  const settings = {
    speed: 800,
    dots: false,
    arrows: false,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    beforeChange: (current, next) => setCurrentIndex(next),
  };

  const handlePrevious = () => {
    carouselRef.current.slickPrev();
  };

  const handleNext = () => {
    carouselRef.current.slickNext();
  };

  // Reserve the carousel's box while the banners request is in flight. Without
  // this the hero renders as an empty <Slider> with no height, then jumps to
  // full size when the data lands — the largest layout shift on the page, and it
  // happens directly above the fold. The paddingTop matches CarouselItem's.
  if (!slides.length) {
    if (error || settled) {
      return null;
    }

    return (
      <Paper
        aria-busy="true"
        sx={{ position: 'relative', paddingTop: { xs: '100%', md: '35%' } }}
      >
        <Skeleton variant="rectangular" animation="wave" sx={{ position: 'absolute', inset: 0, height: '100%' }} />
      </Paper>
    );
  }

  return (
    <>
      <Slider ref={carouselRef} {...settings}>
        {slides.map((item, index) => (
          <CarouselItem
            key={item.id}
            item={item}
            isActive={index === currentIndex}
            isFirst={index === currentIndex}
          />
        ))}
      </Slider>

      <CarouselControlsArrowsIndex
        index={currentIndex}
        total={slides.length}
        onNext={handleNext}
        onPrevious={handlePrevious}
        sx={{ bottom: theme.spacing(25),}}
     
      />
    </>
  );
}
